import React, { useState } from 'react';
import styled from 'styled-components';
import { FiLock, FiUser, FiZap } from 'react-icons/fi';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: #F4F7F9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 48px;
  box-shadow: 0 4px 20px rgba(0, 119, 182, 0.15);
  width: 100%;
  max-width: 420px;
  border: 1px solid #d0d7de;
`;

const Title = styled.h1`
  color: #0077B6;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  text-align: center;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const TitleIcon = styled(FiZap)`
  color: #FFC107;
  font-size: 1.75rem;
`;

const Subtitle = styled.p`
  color: #666666;
  font-size: 0.85rem;
  text-align: center;
  margin: 0 0 32px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 1.2rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 2px solid #d0d7de;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #333333;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #0077B6;
    box-shadow: 0 0 0 4px rgba(0, 119, 182, 0.1);
  }

  &::placeholder {
    color: #999999;
  }
`;

const LoginButton = styled.button`
  background: white;
  color: #333333;
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 12px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 10px;

  &:hover {
    background: #f5f5f5;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: #ffe5e5;
  color: #B00020;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  border: 1px solid #ffb3b3;
`;

const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        // Store session in localStorage
        localStorage.setItem('sessionId', data.sessionId);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user, data.sessionId);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Title>
          TimeWatt
          <TitleIcon />
        </Title>
        <Subtitle>Please sign in to continue</Subtitle>
        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <InputGroup>
            <InputIcon>
              <FiUser />
            </InputIcon>
            <Input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </InputGroup>
          <InputGroup>
            <InputIcon>
              <FiLock />
            </InputIcon>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputGroup>
          <LoginButton type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </LoginButton>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;

